import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { Loader2, Printer, CheckCircle2, Circle, Scale, ArrowRight, Trophy } from "lucide-react";

export default function FaaActionPlan() {
  const [steps, setSteps] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/faa-action-plan")
      .then((r) => { setSteps(r.data.steps); setChecked(r.data.checked || {}); })
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (key) => {
    const next = !checked[key];
    setChecked((c) => ({ ...c, [key]: next }));
    setSaving(key);
    try {
      await api.post("/faa-action-plan", { step: key, done: next });
    } catch {
      setChecked((c) => ({ ...c, [key]: !next }));
      toast.error("Could not save — try again.");
    } finally {
      setSaving(null);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen"><Navbar />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );

  const done = steps.filter((s) => checked[s.key]).length;
  const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;
  const complete = done === steps.length && steps.length > 0;

  return (
    <div className="min-h-screen pb-16">
      <div className="print:hidden"><Navbar /></div>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-primary mb-2">
              <Scale className="h-4 w-4" /> FAA Roadmap
            </span>
            <h1 className="font-display text-3xl md:text-4xl font-bold">Your FAA Action Plan</h1>
            <p className="text-sm text-slate-400 mt-2 max-w-xl leading-relaxed">
              Work top to bottom. Check off each step as you complete it — your progress is saved to your pilot account and follows you across devices.
            </p>
          </div>
          <Button variant="outline" className="rounded-sm border-border print:hidden" onClick={() => window.print()} data-testid="action-plan-print">
            <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
          </Button>
        </div>

        <div className="flex items-center gap-4 mb-8 max-w-md" data-testid="action-plan-progress">
          <Progress value={pct} className="h-2 bg-secondary" />
          <span className="font-mono text-sm text-primary shrink-0">{done}/{steps.length}</span>
        </div>

        <div className="space-y-3" data-testid="action-plan-steps">
          {steps.map((s, i) => {
            const on = !!checked[s.key];
            return (
              <motion.button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                disabled={saving === s.key}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                data-testid={`action-plan-step-${s.key}`}
                className={`w-full text-left flex items-start gap-4 p-4 rounded-sm border transition-colors break-inside-avoid ${
                  on ? "border-primary/50 bg-primary/5" : "border-border/70 bg-card hover:border-primary/40"}`}>
                {on
                  ? <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                  : <Circle className="h-6 w-6 text-muted-foreground shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-muted-foreground">STEP {i + 1}</span>
                    {on && <span className="text-[10px] font-mono uppercase tracking-widest text-primary">Done</span>}
                  </div>
                  <div className={`font-medium text-[15px] ${on ? "text-foreground" : ""}`}>{s.label}</div>
                  {s.hint && <p className="text-xs text-slate-400 mt-1 leading-relaxed">{s.hint}</p>}
                </div>
              </motion.button>
            );
          })}
        </div>

        {complete ? (
          <div className="mt-8 p-6 rounded-sm border border-primary/40 bg-primary/5 text-center" data-testid="action-plan-complete">
            <Trophy className="h-8 w-8 text-primary mx-auto mb-2" />
            <p className="font-display font-bold text-lg">Action plan complete — you're flying legally.</p>
            <p className="text-sm text-slate-400 mt-1">Keep this handy and stay current with recurrent training every 24 months.</p>
          </div>
        ) : (
          <div className="mt-8 p-5 rounded-sm border border-accent/30 bg-accent/5 flex items-center justify-between gap-3 flex-wrap print:hidden">
            <p className="text-sm text-slate-300">Need a refresher on any step?</p>
            <Link to="/module/gs-regulations" data-testid="action-plan-back-to-module">
              <Button variant="outline" className="rounded-sm">
                Back to the FAA Roadmap <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}

        <p className="mt-8 text-xs text-muted-foreground border-t border-border/50 pt-4">
          This checklist summarizes current FAA pathways for informational purposes only and is not legal advice. Always verify current requirements at faa.gov.
        </p>
      </main>
    </div>
  );
}
